// The ED board: summary, filters, and every patient in the department by zone. Counts and times
// are for display; breach and warning come from the rules engine's recorded alerts.

import type { BoardEntry } from '@diagnuvo/domain';
import { useState } from 'react';
import {
  ageInYears,
  ago,
  alertHandling,
  byZone,
  countdown,
  displayName,
  filterRows,
  fourHour,
  fourHourShare,
  hoursMinutes,
  imagingText,
  initials,
  labsText,
  sexLetter,
  summarise,
  timeInDepartment,
  triageText,
  ZONE_LABELS,
  zoneRank,
  type Filters,
  type Rows,
  type StatusFilter,
} from '../board-state.ts';
import { Banner, ZoneDot } from '../components/Layout.tsx';
import {
  AlertIcon,
  BedIcon,
  ChevronIcon,
  ClockIcon,
  FlaskIcon,
  HeartIcon,
  InfoIcon,
  PatientIcon,
  ScanIcon,
} from '../icons.tsx';
import { DEMO_VALUE_SITES, rowDemo, type DemoStatus } from '../patient-panels.ts';
import { href } from '../router.ts';
import type { StreamStatus } from '../use-board-stream.ts';

const STATUS_TEXT: Record<StreamStatus, string> = {
  connecting: 'Connecting…',
  live: 'Live',
  reconnecting: 'Reconnecting…',
};

export function BoardPage({
  siteId,
  rows,
  status,
  now,
  filters,
  onFilters,
}: {
  siteId: string;
  rows: Rows;
  status: StreamStatus;
  now: number;
  filters: Filters;
  onFilters: (f: Filters) => void;
}) {
  const summary = summarise(rows, now);
  // Site SYN only: the Manus status band of each row's predefined value (patient-panels.ts).
  const demo = DEMO_VALUE_SITES.has(siteId);
  const [demoFilter, setDemoFilter] = useState<DemoStatus | undefined>(undefined);
  const demoCount = (s: DemoStatus) =>
    [...rows.values()].filter((r) => rowDemo(r)?.status === s).length;
  const filtered = filterRows(rows, filters);
  const shown =
    demo && demoFilter !== undefined
      ? new Map([...filtered].filter(([, r]) => rowDemo(r)?.status === demoFilter))
      : filtered;
  const zones = byZone(shown);
  const zoneCodes = [...new Set([...rows.values()].map((r) => r.location?.room ?? ''))].filter(
    (c) => c !== '',
  );
  const setStatus = (s: StatusFilter) =>
    onFilters({ ...filters, status: filters.status === s ? 'all' : s });

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="title-row">
            <span className="phase">PHASE 1</span>
            <h1>ED OPERATIONS BOARD</h1>
          </div>
          <p className="subtitle">
            Synthetic Hospital ({siteId}) · every patient in the department
          </p>
        </div>
        <p className={`live live-${status}`} role="status">
          <span className="dot" /> {STATUS_TEXT[status]}
        </p>
      </div>

      <Banner>
        Synthetic demonstration data. Times and values are shown as the hospital systems sent them;
        breaches and warnings are the ED 4-hour standard timer. Nothing here is a prediction or a
        clinical decision.
      </Banner>

      <div className="stats">
        <Stat
          icon={<PatientIcon />}
          label="In department"
          value={summary.inDepartment}
          caption="Arrived and not yet left the ED"
        />
        <Stat
          icon={<AlertIcon />}
          tone="danger"
          label="Breached"
          value={summary.breached}
          caption="Over 4 hours in the ED"
          active={filters.status === 'breach'}
          onClick={() => setStatus('breach')}
        />
        <Stat
          icon={<ClockIcon />}
          tone="warning"
          label="Warning"
          value={summary.warning}
          caption="Over 3 hours, under 4"
          active={filters.status === 'warning'}
          onClick={() => setStatus('warning')}
        />
        <Stat
          icon={<BedIcon />}
          tone="info"
          label="Awaiting a bed"
          value={summary.admittedInEd}
          caption="Admitted, still in the ED"
          active={filters.status === 'admitted'}
          onClick={() => setStatus('admitted')}
        />
        <Stat
          icon={<FlaskIcon />}
          label="Results pending"
          value={summary.resultsPending}
          caption="Labs or imaging not yet back"
        />
        <Stat
          icon={<ClockIcon />}
          label="Longest wait"
          value={summary.longestWait === undefined ? '—' : hoursMinutes(summary.longestWait.ms)}
          caption={
            summary.longestWait === undefined
              ? 'Nobody waiting'
              : `${displayName(summary.longestWait.row.patient)}`
          }
        />
      </div>

      {demo && (
        <section className="demo-stats" aria-label="Demonstration status">
          <p className="demo-stats-head">
            <strong>Demonstration status</strong> · predefined Manus scenario values by each visit's
            recorded milestones. Not calculated from patient data; not a clinical assessment.
          </p>
          <div className="stats stats-demo">
            {(['Critical', 'Urgent', 'Stable'] as const).map((s) => (
              <Stat
                key={s}
                icon={<InfoIcon />}
                label={`${s} (demo)`}
                value={demoCount(s)}
                caption={
                  s === 'Critical'
                    ? 'Predefined score 70 and over'
                    : s === 'Urgent'
                      ? 'Predefined score 20 to 69'
                      : 'Predefined score under 20'
                }
                active={demoFilter === s}
                onClick={() => setDemoFilter(demoFilter === s ? undefined : s)}
              />
            ))}
          </div>
        </section>
      )}

      <div className="filters" role="group" aria-label="Filter the board">
        <button
          type="button"
          className={`chip${filters.zone === 'all' ? ' chip-on' : ''}`}
          onClick={() => onFilters({ ...filters, zone: 'all' })}
        >
          All zones
        </button>
        {zoneCodes
          .sort((a, b) => zoneRank(a) - zoneRank(b) || a.localeCompare(b))
          .map((code) => (
            <button
              key={code}
              type="button"
              className={`chip${filters.zone === code ? ' chip-on' : ''}`}
              onClick={() => onFilters({ ...filters, zone: filters.zone === code ? 'all' : code })}
            >
              {ZONE_LABELS[code] ?? code}
            </button>
          ))}
        {(filters.status !== 'all' ||
          filters.zone !== 'all' ||
          filters.query !== '' ||
          demoFilter !== undefined) && (
          <button
            type="button"
            className="chip chip-clear"
            onClick={() => {
              onFilters({ zone: 'all', status: 'all', query: '' });
              setDemoFilter(undefined);
            }}
          >
            Clear filters
          </button>
        )}
        <span className="filters-count">
          Showing {shown.size} of {rows.size}
        </span>
      </div>

      {zones.map((zone) => {
        const breached = zone.rows.filter((r) => fourHour(r)?.alert === 'breach').length;
        return (
          <section key={zone.code} className="card zone">
            <header className="zone-head" data-zone={zone.code}>
              <ZoneDot code={zone.code} />
              <h2>{zone.label}</h2>
              <span className="zone-count">
                {zone.rows.length} patient{zone.rows.length === 1 ? '' : 's'}
              </span>
              {breached > 0 && <span className="pill pill-danger">{breached} breached</span>}
            </header>
            <ul className="rows">
              {[...zone.rows]
                .sort((a, b) => timeLeft(a, now) - timeLeft(b, now))
                .map((row) => (
                  <PatientRow key={row.encounterId} row={row} now={now} demo={demo} />
                ))}
            </ul>
          </section>
        );
      })}

      {rows.size > 0 && shown.size === 0 && (
        <p className="empty">No patient matches these filters.</p>
      )}
      {rows.size === 0 && status === 'live' && (
        <p className="empty">Nobody is in the department.</p>
      )}
    </div>
  );
}

/** Milliseconds to the 4-hour breach (negative once past); no timer sorts last. */
function timeLeft(row: BoardEntry, now: number): number {
  const timer = fourHour(row);
  if (timer === undefined || timer.stoppedAt !== undefined) return Number.MAX_SAFE_INTEGER;
  return Date.parse(timer.breachAt) - now;
}

function Stat({
  icon,
  label,
  value,
  caption,
  tone = 'neutral',
  active = false,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  caption: string;
  tone?: 'neutral' | 'danger' | 'warning' | 'info';
  active?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className="stat-label">
        {icon} {label}
      </span>
      <span className="stat-value">{value}</span>
      <span className="stat-caption">{caption}</span>
    </>
  );
  return onClick === undefined ? (
    <div className={`stat stat-${tone}`}>{body}</div>
  ) : (
    <button
      type="button"
      className={`stat stat-${tone} stat-button${active ? ' stat-on' : ''}`}
      aria-pressed={active}
      onClick={onClick}
    >
      {body}
    </button>
  );
}

function PatientRow({ row, now, demo }: { row: BoardEntry; now: number; demo: boolean }) {
  const predefined = demo ? rowDemo(row) : undefined;
  const p = row.patient;
  const age = ageInYears(p?.birthDate, now);
  const timer = fourHour(row);
  const left = timer === undefined ? undefined : countdown(timer, now);
  const share = fourHourShare(row, now);
  const alert = left?.alert ?? 'none';
  const statusText = alert === 'breach' ? 'Breached' : alert === 'warning' ? 'Warning' : 'On track';
  const handling = alertHandling(timer);
  return (
    <li className={`row row-${alert}`} data-encounter={row.encounterId}>
      <a
        className={`row-link${demo ? ' with-demo' : ''}`}
        href={href({ page: 'patient', encounterId: row.encounterId })}
      >
        <span className="row-status">
          <span className={`status status-${alert}`}>
            {alert === 'none' ? <ClockIcon size={16} /> : <AlertIcon size={16} />}
            {statusText}
          </span>
          {handling !== undefined && (
            <span className="status-note" data-testid="alert-handling">
              {handling}
            </span>
          )}
        </span>
        <span className="row-who">
          <span className="row-avatar" aria-hidden="true">
            {initials(p)}
          </span>
          <span className="row-patient">
            <span className="row-name">{displayName(p)}</span>
            <span className="row-meta">
              {[age === undefined ? undefined : `${age}${sexLetter(p?.gender)}`, p?.mrn]
                .filter((v) => v !== undefined)
                .join(' · ')}
              {row.status === 'admitted' && (
                <span className="pill pill-info">Admitted · awaiting bed</span>
              )}
            </span>
          </span>
        </span>
        <span className="row-presentation">
          {row.triage === undefined ? (
            <span className="muted small">Not triaged</span>
          ) : (
            <span className="pill pill-muted" data-testid="triage">
              {triageText(row.triage)}
            </span>
          )}
          <span className="row-complaint">{row.complaint?.text ?? 'No complaint recorded'}</span>
        </span>
        {demo && (
          <span className="row-demo" data-testid="demo-status">
            {predefined === undefined ? (
              <span className="muted small">No demo stage</span>
            ) : (
              <>
                <span className="pill pill-muted">
                  {predefined.status} ({predefined.stage.priorityScore}/100)
                </span>
                <span className="small">Demo risk {predefined.stage.risk}%</span>
              </>
            )}
          </span>
        )}
        <span className="row-bed">
          <ZoneDot code={row.location?.room} />
          <BedIcon size={16} /> {row.location?.bed ?? '—'}
        </span>
        <span className="row-time">
          <span className="row-time-top">
            <span>{timeInDepartment(row, now) ?? '—'}</span>
            <span className={`row-left left-${alert}`}>{left?.text ?? ''}</span>
          </span>
          {share !== undefined && (
            <span className="bar" aria-hidden="true">
              <span
                className={`bar-fill fill-${alert}`}
                style={{ width: `${Math.min(share, 1) * 100}%` }}
              />
              <span className="bar-mark" />
            </span>
          )}
        </span>
        <span className="row-chips">
          <span className="mini">
            <FlaskIcon size={15} /> {labsText(row)}
          </span>
          <span className="mini">
            <ScanIcon size={15} /> {imagingText(row)}
          </span>
          <span className="mini">
            <HeartIcon size={15} /> {ago(row.latestVitalsAt, now) ?? 'No observations'}
          </span>
        </span>
        <ChevronIcon />
      </a>
    </li>
  );
}
